import { useEffect, useMemo } from 'react';
import { Provider, useDispatch } from 'react-redux';
import store from 'client/store/store';
import { HelmetProvider } from 'react-helmet-async';
import { setOrganisationId, MsalProviderWrapper } from 'client/utils/auth/msalHelpers';
import IntlProvider from 'i18n/IntlProvider/IntlProvider';
import AuthProvider from 'client/utils/auth/AuthProvider/AuthProvider';
import OrganisationGate from 'client/utils/auth/OrganisationGate';
import { OverlayProvider } from 'react-aria';
import { MapProvider } from 'os-elements';
import { createBrowserRouter, RouterProvider } from 'react-router';
import routes from 'client/utils/routes/routes';
import { fetchMyCoverageOnce } from 'client/store/coverageAssetOwnersSlice/coverageAssetOwnersSlice';
import useAuth from 'client/utils/auth/useAuth';

import styles from './App.module.scss';

const Router = () => {
    const { user } = useAuth();
    const dispatch = useDispatch();

    // Data Routers should not be held in React state. Create your router once and pass it to <RouterProvider>
    // https://reactrouter.com/api/data-routers/createBrowserRouter#summary

    // But MSAL initialization is async and incomplete at module load time, so we must create it here inside the component.
    // router is being created across renders, causing multiple loaders
    // useMemo only creates the router once and will not re-create on subsequent renders
    const router = useMemo(() => createBrowserRouter(routes, {
        future: {
            v7_relativeSplatPath: true,
            v7_startTransition: true,
            v7_fetcherPersist: true,
            v7_normalizeFormMethod: true,
            v7_partialHydration: false,
            v7_skipActionErrorRevalidation: true
        }
    }), []);

    // fetch coverage on app load + authenticated
    useEffect(() => {
        if (user) {
            dispatch(fetchMyCoverageOnce());
        }
    }, [user, dispatch]);

    return <RouterProvider router={router} />;
};

export const App = () => {
    return (
        <Provider store={store}>
            <HelmetProvider>
                <IntlProvider>
                    <MsalProviderWrapper>
                        <AuthProvider>
                            <OverlayProvider>
                                <div
                                    data-testid="app"
                                    className={['govuk-template__body', styles.container].join(', ')}
                                >
                                    <OrganisationGate setOrganisationId={setOrganisationId}>
                                        <MapProvider>
                                            <Router />
                                        </MapProvider>
                                    </OrganisationGate>
                                </div>
                            </OverlayProvider>
                        </AuthProvider>
                    </MsalProviderWrapper>
                </IntlProvider>
            </HelmetProvider>
        </Provider>

    );
};

export default App;
